// ============================================================================
// BehaviorShiftOverview.tsx
// Section component for displaying behavior incidents by shift
// Similar to Fall Analysis Shift and Unit section (Morning, Afternoon, Night only)
// ============================================================================

import { Bar } from "react-chartjs-2";
import { Fragment, useEffect, useState } from "react";
import { Container, Child, Header, LayerContent } from "../styles";
import { ChartContainer, NoData } from "../../style";
import { useAnalyticsWrapper } from "../../../../../store/store";
import {
  barOptions,
  handlePointClicked,
} from "../formUtils";
import SectionModal from "./BehaviorAnalysisSectionModal";
import {
  handleCSVDownload,
  handlePDFDownload,
} from "../../../../../utils/helpers/pdfCsvUtils";
import { behaviorAnalysisDetailCols } from "../../dummyData/colums";
import { Grid, GridColumn, GridColumnProps } from "@progress/kendo-react-grid";
import {
  CenteredWholeNumCell,
  HeaderCenteredCell,
} from "../../../../../components/DynamicGrid/customCells";
import { TotalRow } from "../../../../../components/DynamicGrid/customRows";
import useFormDataHelpers from "../../../../../hooks/useFormDataHelpers";
import { MITIGATE_COLORS } from "../../../../../shared/sharedConstants";

// ============================================================================
// Props Interface
// ============================================================================
interface BehaviorShiftOverviewProps {
  setClickedIncident: (item: any) => void;
  isFetchingSelectedIncident: boolean;
  shareTableData?: (data: any[]) => void;
}

// ============================================================================
// Row Type Definition
// ============================================================================
type ShiftRow = {
  shift: string;
  count: number;
  percentage: number;
};

// ============================================================================
// Component
// ============================================================================
const BehaviorShiftOverview = ({
  setClickedIncident,
  isFetchingSelectedIncident,
  shareTableData,
}: BehaviorShiftOverviewProps) => {
  // ---------------------------------------------------------------------------
  // Helper Hooks
  // ---------------------------------------------------------------------------
  const { getFacilityLabel } = useFormDataHelpers();

  // ---------------------------------------------------------------------------
  // Local State
  // ---------------------------------------------------------------------------
  const [tableData, setTableData] = useState<ShiftRow[]>([]);
  const [grid, setGrid] = useState<any>([]);
  const [barData, setBarData] = useState<any>([]);
  const [modalData, setModalData] = useState<any>([]);
  const [shift, setShift] = useState("");
  const [showModal, setShowModal] = useState(false);

  // ---------------------------------------------------------------------------
  // Global State from Zustand
  // ---------------------------------------------------------------------------
  const { startDate, endDate, behaviorFacility, behaviorAnalysisIncidents } =
    useAnalyticsWrapper((state) => ({
      startDate: state.startDate,
      endDate: state.endDate,
      behaviorFacility: state.behaviorFacility,
      behaviorAnalysisIncidents: state.behaviorAnalysisIncidents,
    }));

  // Derived value for facility name
  const facilityLabel = getFacilityLabel(behaviorFacility);

  // ---------------------------------------------------------------------------
  // Shift order for consistent display
  // ---------------------------------------------------------------------------
  const SHIFT_ORDER = ["7am - 3pm", "3pm - 11pm", "11pm - 7am"];

  // ---------------------------------------------------------------------------
  // Data Transformation Function - uses shiftBreakdown array from API
  // ---------------------------------------------------------------------------
  const adjustedTableData = (shiftBreakdown: any[]): ShiftRow[] => {
    if (!shiftBreakdown || !Array.isArray(shiftBreakdown) || shiftBreakdown.length === 0) {
      return [];
    }

    let totalIncidentsSum = 0;

    // Sort shifts in preferred order and transform
    const sortedData = SHIFT_ORDER.map((shiftName) => {
      const shiftData = shiftBreakdown.find((item) => item.shift === shiftName);
      const count = shiftData?.count || 0;
      totalIncidentsSum += count;
      return {
        shift: shiftName,
        count: count,
        percentage: shiftData?.percentage || 0,
      };
    });

    // Add totals row
    sortedData.push({
      shift: "Total",
      count: totalIncidentsSum,
      percentage: 100,
    });

    return sortedData;
  };

  // ---------------------------------------------------------------------------
  // Transform table data for bar chart (ensures chart order matches table order)
  // ---------------------------------------------------------------------------
  const transformTableDataForChart = (rows: ShiftRow[]) => {
    // Filter out the totals row for the chart
    const dataRows = rows.filter(row => row.shift !== "Total");
    
    if (dataRows.length === 0) {
      return { labels: [], datasets: [] };
    }

    const labels = dataRows.map(row => row.shift);
    const data = dataRows.map(row => row.count);

    // Colors for Morning, Afternoon, Night
    const shiftColors = [
      MITIGATE_COLORS.ORANGE[0], // Morning - orange
      MITIGATE_COLORS.BLUE[0],   // Afternoon - blue
      MITIGATE_COLORS.PURPLE[0], // Night - purple
    ];

    return {
      labels,
      datasets: [
        {
          label: "# of Incidents",
          data,
          backgroundColor: shiftColors.slice(0, labels.length),
          borderWidth: 0,
        },
      ],
    };
  };

  // ---------------------------------------------------------------------------
  // Process data when behaviorAnalysisIncidents changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (behaviorAnalysisIncidents !== undefined) {
      const shiftSection = behaviorAnalysisIncidents?.shiftSection;
      // Use shiftBreakdown array which has pre-calculated counts and percentages
      const shiftBreakdown = shiftSection?.shiftBreakdown || [];
      
      const transformedData = adjustedTableData(shiftBreakdown);
      const transformedBarData = transformTableDataForChart(transformedData);

      setTableData(transformedData);
      setBarData(transformedBarData);
      shareTableData?.(transformedData);
    }
  }, [behaviorAnalysisIncidents]);

  const title = "Overview of Shift";

  // ---------------------------------------------------------------------------
  // Handle clicking a bar in the chart
  // ---------------------------------------------------------------------------
  const handleBarClicked = (chart: any) => {
    if (!chart.length) return;
    const label = handlePointClicked(chart, barData?.labels);
    setShowModal(true);
    setShift(label);
    // Get drilldown data for the clicked shift from shiftSection
    const drilldownData = behaviorAnalysisIncidents?.shiftSection?.shiftDrilldown || {};
    const filteredDetails = drilldownData[label] || [];
    setModalData(filteredDetails);
  };

  // ---------------------------------------------------------------------------
  // Build the grid component when tableData changes
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const cellCountProps = {
      cells: { data: CenteredWholeNumCell, headerCell: HeaderCenteredCell },
    };
    const cellPercentageProps = {
      cells: {
        data: CenteredWholeNumCell,
        append: "%",
        headerCell: HeaderCenteredCell,
      },
    };

    const grid = (
      <Fragment>
        <Grid
          data={tableData || []}
          scrollable="none"
          rows={{ data: TotalRow }}
        >
          <GridColumn field="shift" title="Shift" />
          <GridColumn
            field="count"
            title="# of Incidents"
            {...(cellCountProps as GridColumnProps)}
          />
          <GridColumn
            field="percentage"
            title="%"
            {...(cellPercentageProps as GridColumnProps)}
          />
        </Grid>
      </Fragment>
    );
    setGrid(grid);
  }, [tableData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <LayerContent>
      <Header>{title}</Header>
      <Container>
        <Child flex="40%">
          {grid}
        </Child>
        <Child flex="60%">
          <ChartContainer>
            {barData?.labels?.length > 0 ? (
              <Bar options={barOptions(handleBarClicked)} data={barData} />
            ) : (
              <NoData>No Data</NoData>
            )}
          </ChartContainer>
        </Child>
      </Container>

      {showModal && (
        <SectionModal
          open={showModal}
          facilityName={facilityLabel}
          startDate={startDate}
          endDate={endDate}
          closeFn={() => setShowModal(false)}
          resetFn={() => {
            setShift("");
            setModalData([]);
          }}
          isLoading={isFetchingSelectedIncident}
          tableData={modalData}
          tableCols={behaviorAnalysisDetailCols()}
          title={`${title} - ${shift}`}
          getUserCallback={(item: any) => setClickedIncident(item)}
          handlePdfExport={() =>
            handlePDFDownload({
              data: modalData,
              columnData: behaviorAnalysisDetailCols(),
              fileName: facilityLabel,
              startDate,
              endDate,
              subTitle: `${title} - ${shift}`,
            })
          }
          handleCsvExport={() =>
            handleCSVDownload(
              modalData,
              behaviorAnalysisDetailCols(),
              `${facilityLabel} - ${title} - ${shift}`
            )
          }
        />
      )}
    </LayerContent>
  );
};

export default BehaviorShiftOverview;
